import { authService } from '@bri/shared-components';

export interface ProjectType {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  isAvailable: boolean;
}

export interface CreateProjectRequest {
  name: string;
  description?: string;
  projectType: string;
}

export interface Project {
  id: string;
  workspaceId: string;
  name: string;
  description?: string;
  projectType: string;
  createdDate: string;
  createdBy: string;
  lastUpdatedDate: string;
  lastUpdatedBy: string;
  status: 'draft' | 'published' | 'archived';
}

interface ListProjectsOptions {
  limit?: number;
  projectType?: string;
}

interface InsightProjectRecord {
  id?: string;
  workspaceId?: string;
  caption?: string;
  synopsis?: string;
  createdDate?: string;
  createdBy?: string;
  lastUpdatedDate?: string;
  lastUpdatedBy?: string;
  publicationStatus?: string;
  contents?: {
    name?: string;
    description?: string;
    projectType?: string;
  };
}

function normalizeProjectStatus(status?: string): Project['status'] {
  if (status === 'published' || status === 'archived') {
    return status;
  }

  return 'draft';
}

// Project types based on the screenshot
export const PROJECT_TYPES: ProjectType[] = [
  {
    id: 'qualitative-analyzer',
    name: 'Qualitative Analyzer',
    description: 'AI-powered analysis of qualitative data',
    icon: '🤖',
    color: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    isAvailable: true
  },
  {
    id: 'synthetic-research',
    name: 'Synthetic Research',
    description: 'Decision-grade simulation with calibrated synthetic panels',
    icon: '🧬',
    color: 'bg-teal-50 border-teal-200 text-teal-700',
    isAvailable: false
  },
  {
    id: 'qualitative-survey',
    name: 'Qualitative Survey',
    description: 'In-depth insights through open-ended questions',
    icon: '📋',
    color: 'bg-blue-50 border-blue-200 text-blue-700',
    isAvailable: false
  },
  {
    id: 'analyze-text',
    name: 'Analyze Text',
    description: 'Extract insights from text content',
    icon: '📄',
    color: 'bg-green-50 border-green-200 text-green-700',
    isAvailable: false
  },
  {
    id: 'ad-testing',
    name: 'Ad Testing',
    description: 'Test and optimize advertising campaigns',
    icon: '🎯',
    color: 'bg-red-50 border-red-200 text-red-700',
    isAvailable: false
  },
  {
    id: 'concept-test',
    name: 'Concept Test',
    description: 'Validate ideas and concepts with users',
    icon: '💡',
    color: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    isAvailable: false
  },
  {
    id: 'product-test',
    name: 'Product Test',
    description: 'Test products with real users',
    icon: '📦',
    color: 'bg-orange-50 border-orange-200 text-orange-700',
    isAvailable: false
  },
  {
    id: 'video-quick-poll',
    name: 'Video Quick Poll',
    description: 'Fast video-based polling and feedback',
    icon: '📹',
    color: 'bg-purple-50 border-purple-200 text-purple-700',
    isAvailable: false
  }
];

class ProjectService {
  private baseUrl: string;

  constructor() {
    // Get the insights objects service URL from environment or use the deployed dev endpoint
    const insightsApiUrl = import.meta.env.VITE_INSIGHTS_API_URL || 'https://od8y0furr8.execute-api.us-east-1.amazonaws.com/dev';
    this.baseUrl = `${insightsApiUrl}/insightsobjects`;
  }

  private async getHeaders(): Promise<HeadersInit> {
    const token = await authService.getValidToken();

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }

  async createProject(projectData: CreateProjectRequest): Promise<Project> {
    try {
      const headers = await this.getHeaders();
      
      // Create the project as an insight object with objectClass 'project'
      const insightObject = {
        objectClass: 'project',
        contents: {
          name: projectData.name,
          description: projectData.description,
          projectType: projectData.projectType,
        },
        caption: projectData.name,
        synopsis: projectData.description || '',
        publicationStatus: 'draft',
        tags: {
          objectTags: ['project', projectData.projectType]
        }
      };

      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(insightObject),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Create project error response:', errorText);
        throw new Error(`Failed to create project: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      
      // Handle the API response format: {error: false, insights: [...]}
      let createdInsight;
      if (result.insights && result.insights.length > 0) {
        createdInsight = result.insights[0]; // Take the first (and should be only) insight
      } else if (result.insight) {
        createdInsight = result.insight; // Alternative response format
      } else {
        createdInsight = result; // Direct insight object
      }
      
      // Transform the insight object back to our project format
      return this.transformInsightToProject(createdInsight);
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  }

  async listProjects(_workspaceId?: string, options: ListProjectsOptions = {}): Promise<Project[]> {
    try {
      const headers = await this.getHeaders();
      
      const params = new URLSearchParams({
        objectClass: 'project',
        limit: String(options.limit ?? 50),
        sortBy: 'lastUpdatedDate',
        sortOrder: 'desc'
      });

      if (options.projectType) {
        params.set('projectType', options.projectType);
      }

      // Note: We don't add workspaceId as a query param because the insights service
      // gets the workspaceId from the JWT token via the authorizer
      // The workspaceId parameter is kept for potential future use

      const url = `${this.baseUrl}?${params}`;

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('List projects error response:', errorText);
        
        // If it's a 401, provide more detailed error info
        if (response.status === 401) {
          console.error('Authentication failed. This could be due to:');
          console.error('1. Invalid or expired JWT token');
          console.error('2. Missing workspaceId in token claims');
          console.error('3. WorkspaceId in token does not exist in auth service database');
          console.error('4. Token format not recognized by authorizer');
        }
        
        throw new Error(`Failed to list projects: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      
      // Transform insight objects to projects
      return result.insights?.map(this.transformInsightToProject) || [];
    } catch (error) {
      console.error('Error listing projects:', error);
      throw error;
    }
  }

  async getProject(projectId: string): Promise<Project> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(`${this.baseUrl}/${projectId}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get project: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      return this.transformInsightToProject(result);
    } catch (error) {
      console.error('Error getting project:', error);
      throw error;
    }
  }

  private transformInsightToProject(insight: InsightProjectRecord): Project {
    // Add defensive checks for required fields
    if (!insight) {
      throw new Error('Invalid insight object: insight is null or undefined');
    }
    
    return {
      id: insight.id || 'unknown-id',
      workspaceId: insight.workspaceId || 'unknown-workspace',
      name: insight.contents?.name || insight.caption || 'Untitled Project',
      description: insight.contents?.description || insight.synopsis || '',
      projectType: insight.contents?.projectType || 'qualitative-survey',
      createdDate: insight.createdDate || new Date().toISOString(),
      createdBy: insight.createdBy || 'unknown',
      lastUpdatedDate: insight.lastUpdatedDate || insight.createdDate || new Date().toISOString(),
      lastUpdatedBy: insight.lastUpdatedBy || insight.createdBy || 'unknown',
      status: normalizeProjectStatus(insight.publicationStatus),
    };
  }
}

export const projectService = new ProjectService();
